import { useRef } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { type Injectables, Remote } from '@proposify/mfe-tools';
import { trackError } from '@proposify/frontend-observability';
import { NotificationsProvider } from 'components/notifications/NotificationsProvider';
import { I18nProvider } from 'providers/I18nProvider';
import { QueryProvider } from 'providers/QueryProvider';
import { QueryProviderV5 } from 'providers/QueryProviderV5';
import { PreviewByContentId } from 'pages/editor/PreviewByContentId';
import { DesignTokenProvider, designToken } from '@proposify/ui';

export interface DocumentPreviewInjectables extends Injectables {
  contentId: string;
}

const REQUIRED_ENV_VARS = ['VITE_DOCUMENT_SERVICE_API_ENDPOINT', 'VITE_DOCUMENT_SERVICE_API_HOST'] as const;

function assertRequiredEnvVars(): void {
  const missing = REQUIRED_ENV_VARS.filter((key) => !globalThis.env?.[key]);
  if (missing.length > 0) {
    trackError(new Error(`DocumentPreviewMicrofrontend: missing required env vars: ${missing.join(', ')}`));
  }
}

function validateInjectables(injectables: object): asserts injectables is DocumentPreviewInjectables {
  if ('contentId' in injectables) {
    if (typeof injectables.contentId === 'string' && injectables.contentId !== '') {
      return;
    }
  }
  const error = new Error('validation failed, could not load DocumentPreviewMicrofrontend');
  trackError(error, {
    injectablesFromHost: injectables,
  });
  throw error;
}

const DocumentPreviewMicrofrontend = () => {
  const envAssertedRef = useRef(false);
  if (!envAssertedRef.current) {
    envAssertedRef.current = true;
    assertRequiredEnvVars();
  }

  return (
    <Remote name="document-preview-remote" validateInjectables={validateInjectables}>
      {(injectables) => (
        // HelmetProvider is required by react-helmet-async and is added independently in each
        // MFE entrypoint, since this app has no single shared root the provider tree can live in.
        <HelmetProvider>
          <I18nProvider>
            <QueryProvider>
              <QueryProviderV5>
                <DesignTokenProvider theme={designToken}>
                  <NotificationsProvider>
                    <PreviewByContentId contentId={injectables.contentId} />
                  </NotificationsProvider>
                </DesignTokenProvider>
              </QueryProviderV5>
            </QueryProvider>
          </I18nProvider>
        </HelmetProvider>
      )}
    </Remote>
  );
};

const DocumentPreviewMicrofrontendEntry = () => <DocumentPreviewMicrofrontend />;
export default DocumentPreviewMicrofrontendEntry;
